import { useQuery } from '@tanstack/react-query'
import { conversationsApi, type Conversation, type Message } from '@/lib/api'

/**
 * Query for fetching all user conversations
 */
export function useConversations() {
  return useQuery({
    queryKey: ['conversations'],
    queryFn: async (): Promise<Conversation[]> => {
      const response = await conversationsApi.getAll()
      return response.data
    },
    refetchInterval: 10000,
    refetchIntervalInBackground: true,
  })
}

/**
 * Query for fetching messages in a specific conversation
 */
export function useMessages(conversationId: number | null | undefined) {
  return useQuery({
    queryKey: ['conversations', conversationId, 'messages'],
    enabled: typeof conversationId === 'number',
    queryFn: async (): Promise<Message[]> => {
      if (typeof conversationId !== 'number') {
        throw new Error('Conversation ID is required')
      }
      const response = await conversationsApi.getMessages(conversationId)
      return response.data
    },
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
  })
}

/**
 * Derived hook that returns the unread conversation count
 */
export function useUnreadCount() {
  const { data: conversations } = useConversations()

  return (
    conversations?.reduce((total, conversation) => total + (conversation.unread_count || 0), 0) ?? 0
  )
}
